import { useQuery } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';
import type { AxiosRequestConfig } from 'axios';

import { customAxiosInstance } from 'api/mutator/customAxiosInstance';

export type GetCryptoTransactionsParams = {
  per_page?: number;
  cursor_after?: string;
  cursor_before?: string;
  virtual_account_number?: string;
};

export type CryptoTransaction = {
  type: 'deposit' | 'exchange' | 'withdrawal';
  display_type: string;
  record_uuid: string;
  display_at?: string | null;
  status?: string | null;
  layer1_created_at?: string | null;
  asset?: string | null;
  amount?: number | null;
  formatted_amount?: string | null;
  network?: string | null;
  deposit_address?: string | null;
  destination_address?: string | null;
  from_address?: string | null;
  tx_hash?: string | null;
  created_at?: string | null;
  from_currency?: string | null;
  from_network?: string | null;
  from_amount?: number | null;
  formatted_from_amount?: string | null;
  to_currency?: string | null;
  to_amount?: number | null;
  formatted_to_amount?: string | null;
  rate?: number | null;
};

export type CryptoTransactionsMeta = {
  per_page?: number;
  total?: number;
  cursor_next?: string | null;
  cursor_prev?: string | null;
  has_next_page?: boolean;
  has_prev_page?: boolean;
};

export type GetCryptoTransactions200 = {
  data?: CryptoTransaction[];
  meta?: CryptoTransactionsMeta;
};

export const getCryptoTransactions = (
  organisationId: number,
  params?: GetCryptoTransactionsParams,
  options?: AxiosRequestConfig
) => {
  return customAxiosInstance<GetCryptoTransactions200>(
    {
      url: `/crypto/organisations/${organisationId}/transactions`,
      method: 'GET',
      params
    },
    options
  );
};

export const getGetCryptoTransactionsQueryKey = (
  organisationId?: number,
  params?: GetCryptoTransactionsParams
) => {
  return [
    `/crypto/organisations/${organisationId}/transactions`,
    ...(params ? [params] : [])
  ] as const;
};

export const useGetCryptoTransactions = (
  organisationId: number,
  params?: GetCryptoTransactionsParams,
  options?: {
    query?: Omit<
      UseQueryOptions<GetCryptoTransactions200, unknown>,
      'queryKey' | 'queryFn'
    >;
    request?: AxiosRequestConfig;
  }
) => {
  return useQuery({
    queryKey: getGetCryptoTransactionsQueryKey(organisationId, params),
    queryFn: () =>
      getCryptoTransactions(organisationId, params, options?.request),
    enabled: !!organisationId,
    ...options?.query
  });
};
